import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { PortalAuthProvider } from "@/context/PortalAuthContext";
import { I18nProvider } from "@/lib/i18n";
import { BrandProvider } from "@/context/BrandContext";
import ErrorBoundary from "@/components/ErrorBoundary";

// --- Eagerly-loaded shells (critical path) ---
import AppShell from "@/components/app/AppShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";

// --- Lazy-loaded routes (Iter 131.6.55 - page-load speedup) ---
// Every heavy route is now split out of the initial bundle.
// First paint on `/` / `/login` ships ~70-80 % smaller JS; each
// in-app route is fetched on first navigation only. Repeat visits hit
// the browser cache. Suspense fallback is a lightweight splash so the
// page never flashes blank.
const Marketing = lazy(() => import("@/pages/Marketing"));
const Signup = lazy(() => import("@/pages/Signup"));
const AuthMagic = lazy(() => import("@/pages/AuthMagic"));
const SuperAdmin = lazy(() => import("@/pages/SuperAdmin"));
const SuperAdminUboAudit = lazy(() => import("@/pages/SuperAdminUboAudit"));

const Clients = lazy(() => import("@/pages/app/Clients"));
const ClientDetail = lazy(() => import("@/pages/app/ClientDetail"));
const Requests = lazy(() => import("@/pages/app/Requests"));
const Approvals = lazy(() => import("@/pages/app/Approvals"));
const Invoices = lazy(() => import("@/pages/app/Invoices"));
const Bills = lazy(() => import("@/pages/app/Bills"));
const Invoicing = lazy(() => import("@/pages/app/Invoicing"));
const R4Redirect = lazy(() => import("@/components/app/R4Redirect"));
const Contracts = lazy(() => import("@/pages/app/Contracts"));
const Tenancies = lazy(() => import("@/pages/app/Tenancies"));
const ContractDetail = lazy(() => import("@/pages/app/ContractDetail"));
const ReconciliationScope = lazy(() => import("@/pages/app/ReconciliationScope"));
const RoleReview = lazy(() => import("@/pages/app/RoleReview"));
const R3Safety = lazy(() => import("@/pages/app/R3Safety"));
const ReconciliationQueue = lazy(() => import("@/pages/app/ReconciliationQueue"));
const Reconciliation = lazy(() => import("@/pages/app/Reconciliation"));
const Proformas = lazy(() => import("@/pages/app/Proformas"));
const ProformaConvert = lazy(() => import("@/pages/app/ProformaConvert"));
const IssuersManage = lazy(() => import("@/pages/app/IssuersManage"));
const OurCompany = lazy(() => import("@/pages/app/OurCompany"));
const Costs = lazy(() => import("@/pages/app/Costs"));
const Pricelist = lazy(() => import("@/pages/app/Pricelist"));
const BankStatements = lazy(() => import("@/pages/app/BankStatements"));
const BankReviewQueue = lazy(() => import("@/pages/app/BankReviewQueue"));
const DocReader = lazy(() => import("@/pages/app/DocReader"));
const Leads = lazy(() => import("@/pages/app/Leads"));
const Timesheets = lazy(() => import("@/pages/app/Timesheets"));
const UserManual = lazy(() => import("@/pages/app/UserManual"));
const BusinessRules = lazy(() => import("@/pages/app/BusinessRules"));
const TenantAdmin = lazy(() => import("@/pages/app/TenantAdmin"));
const Admin = lazy(() => import("@/pages/app/Admin"));
const MFASetup = lazy(() => import("@/pages/app/MFASetup"));
const Documents = lazy(() => import("@/pages/app/Documents"));
const Banking = lazy(() => import("@/pages/app/Banking"));
const ClientAUA = lazy(() => import("@/pages/app/ClientAUA"));
const LineageSearch = lazy(() => import("@/pages/app/LineageSearch"));
const DedupeDashboard = lazy(() => import("@/pages/app/DedupeDashboard"));
const Transactions = lazy(() => import("@/pages/app/Transactions"));
const StatementImport = lazy(() => import("@/pages/app/StatementImport"));
const R6Accounts = lazy(() => import("@/pages/app/r6/Accounts"));
const R6Transactions = lazy(() => import("@/pages/app/r6/Transactions"));
const R6Lending = lazy(() => import("@/pages/app/r6/Lending"));
const hub = (name) => lazy(() => import("@/pages/app/r6/Hubs").then((m) => ({ default: m[name] })));
const RequestsHub = hub("RequestsHub");
const DocumentsHub = hub("DocumentsHub");
const PeopleHub = hub("PeopleHub");
const ComplianceHub = hub("ComplianceHub");
const SettingsHub = hub("SettingsHub");
const DataReviewHub = hub("DataReviewHub");
const R6 = ({ to, children, keep }) => <R4Redirect flag="R6_sidebar" to={to} keepSearch={keep}>{children}</R4Redirect>;
const DashboardRoute = hub("DashboardRoute");
const Lending = lazy(() => import("@/pages/app/Lending"));
const FXRates = lazy(() => import("@/pages/app/FXRates"));
const Compliance = lazy(() => import("@/pages/app/Compliance"));
const Reports = lazy(() => import("@/pages/app/Reports"));
const Dashboards = lazy(() => import("@/pages/app/Dashboards"));
const PortalUsers = lazy(() => import("@/pages/app/PortalUsers"));
const Payslips = lazy(() => import("@/pages/app/Payslips"));
const PayslipHistory = lazy(() => import("@/pages/app/PayslipHistory"));
const PortalPayslips = lazy(() => import("@/pages/PortalPayslips"));
const People = lazy(() => import("@/pages/app/People"));
const CryptoWallets = lazy(() => import("@/pages/app/CryptoWallets"));
const CryptoAnalyzer = lazy(() => import("@/pages/app/CryptoAnalyzer"));
const CryptoAlerts = lazy(() => import("@/pages/app/CryptoAlerts"));
const CryptoTransactions = lazy(() => import("@/pages/app/CryptoTransactions"));
const Migration = lazy(() => import("@/pages/app/Migration"));
const MigrationSimple = lazy(() => import("@/pages/app/MigrationSimple"));
const MigrationDocsUpload = lazy(() => import("@/pages/app/MigrationDocsUpload"));
const MigrationReconciliation = lazy(() => import("@/pages/app/MigrationReconciliation"));
const ExpensesReview = lazy(() => import("@/pages/app/ExpensesReview"));
const MailConnections = lazy(() => import("@/pages/app/MailConnections"));
const MailScanQueue = lazy(() => import("@/pages/app/MailScanQueue"));
const SecurityDevices = lazy(() => import("@/pages/app/SecurityDevices"));
const RolesSettings = lazy(() => import("@/pages/app/RolesSettings"));
const BrandEditor = lazy(() => import("@/pages/app/BrandEditor"));
const Billing = lazy(() => import("@/pages/app/Billing"));
const BillingSuccess = lazy(() => import("@/pages/app/BillingSuccess"));
const BillingCancel = lazy(() => import("@/pages/app/BillingCancel"));

const PortalShell = lazy(() => import("@/pages/portal/PortalShell"));
const PortalLogin = lazy(() => import("@/pages/portal/PortalLogin"));
const PortalSetPassword = lazy(() => import("@/pages/portal/PortalSetPassword"));
const PortalOverview = lazy(() => import("@/pages/portal/PortalOverview"));
const PortalDocuments = lazy(() => import("@/pages/portal/PortalDocuments"));
const PortalInvoices = lazy(() => import("@/pages/portal/PortalInvoices"));
const PortalRequests = lazy(() => import("@/pages/portal/PortalRequests"));
const PortalMessages = lazy(() => import("@/pages/portal/PortalMessages"));
const PortalSecurity = lazy(() => import("@/pages/portal/PortalSecurity"));
const PortalExpenses = lazy(() => import("@/pages/portal/PortalExpenses"));
const PortalImpersonate = lazy(() => import("@/pages/portal/PortalImpersonate"));

// Minimal fallback - a soft centred spinner so routes don't flash blank.
function RouteFallback() {
  return (
    <div className="min-h-screen grid place-items-center bg-canvas-soft" data-testid="route-fallback">
      <div className="flex items-center gap-3 text-ink-3">
        <div className="w-5 h-5 border-2 border-signal border-t-transparent rounded-full animate-spin" />
        <span className="text-sm">Loading…</span>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback synchronously (before Routes) - must beat any redirects
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Marketing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/auth/magic" element={<AuthMagic />} />
        <Route path="/super-admin" element={<SuperAdmin />} />
        <Route path="/super-admin/ubo-audit" element={<SuperAdminUboAudit />} />
        <Route path="/app" element={<AppShell />}>
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardRoute />} />
          <Route path="clients" element={<Clients />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="our-company" element={<OurCompany />} />
          <Route path="requests" element={<R6 to="/app/hub/requests" keep><Requests /></R6>} />
          <Route path="approvals" element={<R6 to="/app/hub/requests?tab=approvals"><Approvals /></R6>} />
          <Route path="hub/requests" element={<RequestsHub />} />
          <Route path="hub/documents" element={<DocumentsHub />} />
          <Route path="hub/people" element={<PeopleHub />} />
          <Route path="hub/compliance" element={<ComplianceHub />} />
          <Route path="hub/settings" element={<SettingsHub />} />
          <Route path="hub/data-review" element={<DataReviewHub />} />
          <Route path="invoicing" element={<Invoicing />} />
          <Route path="invoices" element={<R4Redirect flag="R4_invoicing" to="/app/invoicing?tab=invoices"><Invoices /></R4Redirect>} />
          <Route path="bills" element={<R4Redirect flag="R4_invoicing" to="/app/costs?tab=supplier-invoices"><Bills /></R4Redirect>} />
          <Route path="supplier-invoices" element={<Navigate to="/app/costs?tab=supplier-invoices" replace />} />
          <Route path="contracts" element={<Contracts />} />
          <Route path="tenancies" element={<Tenancies />} />
          <Route path="contracts/:cid" element={<ContractDetail />} />
          <Route path="reconciliation-scope" element={<R6 to="/app/reconciliation?tab=scope"><ReconciliationScope /></R6>} />
          <Route path="role-review" element={<R6 to="/app/hub/data-review?tab=roles"><RoleReview /></R6>} />
          <Route path="safety-net" element={<R3Safety />} />
          <Route path="reconciliation" element={<Reconciliation />} />
          <Route path="proformas" element={<R4Redirect flag="R4_invoicing" to="/app/invoicing?tab=payment-requests"><Proformas /></R4Redirect>} />
          <Route path="proformas/convert" element={<R6 to="/app/invoicing?tab=convert"><R4Redirect flag="R4_invoicing" to="/app/invoicing?tab=convert"><ProformaConvert /></R4Redirect></R6>} />
          <Route path="issuers" element={<R6 to="/app/hub/settings?tab=issuers"><IssuersManage /></R6>} />
          <Route path="pricelist" element={<R6 to="/app/hub/settings?tab=pricelist"><Pricelist /></R6>} />
          <Route path="bank-statements" element={<R6 to="/app/money/transactions?tab=import"><BankStatements /></R6>} />
          <Route path="doc-reader" element={<R6 to="/app/hub/documents?tab=reader"><DocReader /></R6>} />
          <Route path="reviews" element={<Navigate to="/app/compliance?tab=reviews" replace />} />
          <Route path="dac6" element={<Navigate to="/app/compliance?tab=dac6" replace />} />
          <Route path="crs" element={<Navigate to="/app/compliance?tab=crs" replace />} />
          <Route path="documents" element={<R6 to="/app/hub/documents"><Documents /></R6>} />
          <Route path="banking" element={<R6 to="/app/money/accounts?type=bank"><Banking /></R6>} />
          <Route path="banking/client-aua" element={<R6 to="/app/money/accounts?type=client_aua"><ClientAUA /></R6>} />
          <Route path="lineage" element={<LineageSearch />} />
          <Route path="admin/dedupe" element={<R6 to="/app/hub/data-review?tab=merges"><DedupeDashboard /></R6>} />
          <Route path="banking/review-queue" element={<R6 to="/app/reconciliation?tab=queue"><R4Redirect flag="R5_reconciliation" to="/app/reconciliation?tab=queue"><BankReviewQueue /></R4Redirect></R6>} />
          <Route path="transactions" element={<R6 to="/app/money/transactions"><Transactions /></R6>} />
          <Route path="transactions/statement-import" element={<StatementImport />} />
          <Route path="money/accounts" element={<R6Accounts />} />
          <Route path="money/transactions" element={<R6Transactions />} />
          <Route path="money/lending" element={<R6Lending />} />
          <Route path="lending" element={<R6 to="/app/money/lending"><Lending /></R6>} />
          <Route path="fx" element={<R6 to="/app/hub/settings?tab=fx"><FXRates /></R6>} />
          <Route path="compliance" element={<R6 to="/app/hub/compliance" keep><Compliance /></R6>} />
          <Route path="reports" element={<R6 to="/app/dashboard?tab=reports"><Reports /></R6>} />
          <Route path="dashboards" element={<R6 to="/app/dashboard?tab=kpi"><Dashboards /></R6>} />
          <Route path="payslips" element={<R6 to="/app/hub/people?tab=payslips"><Payslips /></R6>} />
          <Route path="payslips/by-person/:personId" element={<PayslipHistory />} />
          <Route path="people" element={<R6 to="/app/hub/people?tab=people"><People /></R6>} />
          <Route path="crypto-wallets" element={<R6 to="/app/money/accounts?type=crypto"><CryptoWallets /></R6>} />
          <Route path="crypto-analyzer" element={<R6 to="/app/hub/compliance?tab=crypto-analyzer"><CryptoAnalyzer /></R6>} />
          <Route path="crypto-alerts" element={<R6 to="/app/hub/compliance?tab=crypto-alerts"><CryptoAlerts /></R6>} />
          <Route path="crypto-transactions" element={<R6 to="/app/money/transactions?source=crypto"><CryptoTransactions /></R6>} />
          <Route path="migration" element={<ErrorBoundary name="migration"><MigrationSimple /></ErrorBoundary>} />
          <Route path="migration/advanced" element={<Migration />} />
          <Route path="migration/docs-upload" element={<MigrationDocsUpload />} />
          <Route path="migration/reconciliation" element={<MigrationReconciliation />} />
          <Route path="expenses" element={<R6 to="/app/costs?tab=expenses"><R4Redirect flag="R4_invoicing" to="/app/costs?tab=expenses"><ExpensesReview /></R4Redirect></R6>} />
          <Route path="costs" element={<Costs />} />
          <Route path="mail-connections" element={<R6 to="/app/hub/compliance?tab=mail"><MailConnections /></R6>} />
          <Route path="mail-scan-queue" element={<R6 to="/app/hub/compliance?tab=mail-scan"><MailScanQueue /></R6>} />
          <Route path="audit" element={<Navigate to="/app/compliance?tab=audit" replace />} />
          <Route path="leads" element={<Leads />} />
          <Route path="timesheets" element={<R6 to="/app/hub/people?tab=timesheets"><Timesheets /></R6>} />
          <Route path="manual" element={<UserManual />} />
          <Route path="business-rules" element={<R6 to="/app/hub/settings?tab=rules"><BusinessRules /></R6>} />
          <Route path="portal-users" element={<R6 to="/app/hub/settings?tab=portal"><PortalUsers /></R6>} />
          <Route path="tenant-admin" element={<TenantAdmin />} />
          <Route path="admin" element={<R6 to="/app/hub/settings?tab=firm"><Admin /></R6>} />
          <Route path="brand" element={<R6 to="/app/hub/settings?tab=brand"><BrandEditor /></R6>} />
          <Route path="billing" element={<R6 to="/app/hub/settings?tab=billing"><Billing /></R6>} />
          <Route path="billing/success" element={<BillingSuccess />} />
          <Route path="billing/cancel" element={<BillingCancel />} />
          <Route path="mfa" element={<MFASetup />} />
          <Route path="settings/security" element={<SecurityDevices />} />
          <Route path="settings/roles" element={<RolesSettings />} />
        </Route>
        <Route path="/portal/login" element={<PortalLogin />} />
        <Route path="/portal/set-password" element={<PortalSetPassword mode="set" />} />
        <Route path="/portal/reset" element={<PortalSetPassword mode="reset" />} />
        <Route path="/portal/impersonate" element={<PortalImpersonate />} />
        <Route path="/portal/payslips/:token" element={<PortalPayslips />} />
        <Route path="/portal" element={<PortalShell />}>
          <Route index element={<PortalOverview />} />
          <Route path="documents" element={<PortalDocuments />} />
          <Route path="expenses" element={<PortalExpenses />} />
          <Route path="invoices" element={<PortalInvoices />} />
          <Route path="requests" element={<PortalRequests />} />
          <Route path="messages" element={<PortalMessages />} />
          <Route path="security" element={<PortalSecurity />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrandProvider>
        <PortalAuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </PortalAuthProvider>
        </BrandProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
